import * as React from 'react';
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import PromptExit from 'libs/router/prompt-exit/PromptExit';

import { Form, FormActions, FormField } from 'components/form';
import Input from 'components/inputs/input/Input';
import ActionButton from 'components/buttons/action-button/ActionButton';
import PageSection from 'components/page/page-section/PageSection';
import RichTextEditor from 'components/inputs/rich-text-editor/RichTextEditor';
import ProfileSummary from 'modules/misc/components/profile-summary/ProfileSummary';

import { uploadImage } from 'modules/influencers-agency/reducers/influencers-agencies-reducer';
import { danger } from 'redux-store/reducers/common-reducer';

import { __t, applyTranslations } from 'utils/language';
import { getMissingImagesValidationError } from 'utils/profile';
import { addProtocol } from 'utils/url';
import {
  LONG_STRING_MAX_LENGTH,
  NAME_MAX_LENGTH,
  isEmail,
  isRequired,
  isUrl,
  maxLength
} from 'utils/validators';

applyTranslations(require('./influencers-agency-edit-form.i18n.json'));

import { Image, ImageType, InfluencersAgency } from 'types';

const MSG_KEY = 'influencers_agency_edit_form';

type OwnProps = {
  onSubmit: (agencyProfile: InfluencersAgency) => void,
  agency?: InfluencersAgency,
  onCancel?: () => void,
  imageType?: ImageType
}

type DispatchProps = {
  dispatchUploadImage: (type: string, image: Image) => Promise<Image>,
  dispatchDanger: (message: string) => void
};

type Props = OwnProps & DispatchProps;

const InfluencersAgencyEditForm = ({
  agency,
  onSubmit,
  onCancel,
  imageType,
  dispatchUploadImage,
  dispatchDanger
}: Props) => {
  const [agencyProfile, setAgencyProfile] = useState({} as InfluencersAgency);
  const [isModified, setModified] = useState(false);

  useEffect(() => {
    if (agency) {
      setAgencyProfile(agency);
    }
  }, [agency]);

  const addProtocolToUrls = (profileDetails: InfluencersAgency):InfluencersAgency => {
    const keys = ['url', 'instagramUrl', 'youtubeUrl'];
    const updatedProfileDetails = { ...profileDetails };

    keys.forEach(key => {
      updatedProfileDetails[key] = addProtocol(updatedProfileDetails[key]) || null;
    });

    return updatedProfileDetails;
  };

  const handleSubmit = () => {
    const missingImagesError = getMissingImagesValidationError(agencyProfile.coverImage, 'cover')
      || getMissingImagesValidationError(agencyProfile.profileImage, 'profile');

    if (missingImagesError) {
      dispatchDanger(missingImagesError);

      window.scrollTo(0, 0);

      return;
    }

    setModified(false);

    onSubmit(addProtocolToUrls(agencyProfile));
  };

  const handleValidationErrors = () => (
    dispatchDanger(__t(`${MSG_KEY}.messages.missing_fields`))
  );

  const handleChange = (name: string, value: any) => {
    setAgencyProfile((currentProfile) => ({
      ...currentProfile,
      [name]: value
    }));
    setModified(true);
  };

  const sendImage = (type: string, image: Image) => (
    dispatchUploadImage(type, image)
      .then(data => {
        handleChange(`${type}Image`, data);
        return data;
      })
  );

  const handleFieldChanged = (event) => {
    handleChange(event.target.name, event.target.value);
  };

  return (
    <React.Fragment>
      {
        isModified && <PromptExit type="page" />
      }
      <ProfileSummary
        profile={agencyProfile}
        coverImageType={imageType ?? 'cover-medium'}
        sendImage={sendImage}
        canEdit
      />
      <Form
        onSubmit={handleSubmit}
        onValidationErrors={handleValidationErrors}
      >
        <PageSection>
          <FormField
            label={__t(`${MSG_KEY}.name.label`)}
            labelType="highlighted"
            direction="column"
            showRequiredMark
            validators={[
              isRequired(agencyProfile.name),
              maxLength(agencyProfile.name, NAME_MAX_LENGTH)
            ]}
          >
            <Input
              name="name"
              value={agencyProfile.name}
              placeholder={__t(`${MSG_KEY}.name.placeholder`)}
              onChange={handleFieldChanged}
            />
          </FormField>

          <FormField
            label={__t(`${MSG_KEY}.email.label`)}
            labelType="highlighted"
            direction="column"
            showRequiredMark
            tooltipMessage={__t(`${MSG_KEY}.email.tooltip`)}
            validators={[
              isRequired(agencyProfile.email),
              isEmail(agencyProfile.email)
            ]}
          >
            <Input
              name="email"
              value={agencyProfile.email}
              placeholder={__t(`${MSG_KEY}.email.placeholder`)}
              onChange={handleFieldChanged}
            />
          </FormField>

          <FormField
            label={__t(`${MSG_KEY}.website.label`)}
            labelType="highlighted"
            direction="column"
            validators={[
              isUrl(agencyProfile.url, __t(`${MSG_KEY}.website.placeholder`))
            ]}
          >
            <Input
              name="url"
              value={agencyProfile.url}
              width="wide"
              placeholder={__t(`${MSG_KEY}.website.placeholder`)}
              onChange={handleFieldChanged}
            />
          </FormField>

          {
            ['instagram', 'youtube'].map((channelType) => (
              <FormField
                key={`channel-${channelType}`}
                label={__t(`${MSG_KEY}.${channelType}.label`)}
                labelType="highlighted"
                direction="column"
                validators={[
                  isUrl(
                    agencyProfile[`${channelType}Url`],
                    __t(`${MSG_KEY}.${channelType}.placeholder`)
                  )
                ]}
              >
                <Input
                  name={`${channelType}Url`}
                  value={agencyProfile[`${channelType}Url`]}
                  width="wide"
                  placeholder={__t(`${MSG_KEY}.${channelType}.placeholder`)}
                  onChange={handleFieldChanged}
                />
              </FormField>
            ))
          }

          <FormField
            label={__t(`${MSG_KEY}.pitch.label`)}
            labelType="highlighted"
            direction="column"
            showValidationIcon={false}
            validators={[
              maxLength(agencyProfile.pitch, LONG_STRING_MAX_LENGTH)
            ]}
          >
            <RichTextEditor
              key={agencyProfile.pitch}
              canEdit
              value={agencyProfile.pitch}
              onChange={(value) => handleChange('pitch', value)}
            />
          </FormField>

          <FormActions>
            {
              onCancel &&
              <ActionButton
                color="tertiary"
                onClick={onCancel}
              >
                {__t(`${MSG_KEY}.action.cancel`)}
              </ActionButton>
            }
            <ActionButton type="submit">
              {__t(`${MSG_KEY}.action.save`)}
            </ActionButton>
          </FormActions>
        </PageSection>
      </Form>
    </React.Fragment>
  );
};

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchUploadImage: (type, image) => dispatch(uploadImage(type, image)),
  dispatchDanger: message => dispatch(danger(message))
});

export default connect(null, mapDispatchToProps)(InfluencersAgencyEditForm);
